import { z } from "zod";

/**
 * Contract manifest — the single value Agentry pins against.
 *
 * - `CONTRACT_VERSION` は公開契約の内容変更で手動 bump（0.1.0 は非互換2実装が使用したため欠番。DECISION_LOG Q-02/D-011）。
 * - `CONTRACT_HASH` / `CONTRACT_MODULES` は `pnpm contracts:generate` が
 *   schema ソースから**ビルド時に**焼き込む。手で編集しない。
 *   CI は `pnpm contracts:check` で drift と version/hash lock を検証する（INTEGRATION_PLAN.md §2.2）。
 */
export const CONTRACT_VERSION = "0.3.39";
export const CONTRACT_SCHEMA_VERSION = "1";
export const CONTRACT_HASH_ALGORITHM = "sha256";

// ▼ generated by scripts/generate-contract-manifest.mjs — 手で編集しない
export const CONTRACT_MODULES = [
	"agent",
	"analytics",
	"artifact",
	"auth",
	"common",
	"cost",
	"events",
	"git",
	"lp-proposal",
	"lp-research",
	"manager-progress",
	"orders",
	"provider",
	"runtime",
	"session",
	"settings",
	"system",
	"task-board",
	"workflow",
] as const;
export const CONTRACT_HASH = "81d7be96bcc6ade56a309235e98e682c2e9d7c7ff4d42f92609410b107e5626e";
// ▲ generated

export const RouteManifestEntrySchema = z.object({
	method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]),
	path: z.string(), // 例: /v1/orgs/{orgId}/projects/{projectId}/catalog
});
export type RouteManifestEntry = z.infer<typeof RouteManifestEntrySchema>;

export const ContractManifestSchema = z.object({
	contractVersion: z.string(),
	schemaVersion: z.string(),
	hashAlgorithm: z.literal("sha256"),
	contractHash: z.string(),
	modules: z.array(z.string()),
	/** §24.9 外部 Route。Phase 0-4 の Gateway 実装が公開し、Agentry の ENDPOINTS と CI 照合する。 */
	routes: z.array(RouteManifestEntrySchema),
});
export type ContractManifest = z.infer<typeof ContractManifestSchema>;

export const CONTRACT_SERVICES = [
	{
		name: "auth",
		basePath: "/api/auth",
		description: "Users, tenants, roles, JWT issuance, JWKS",
	},
	{
		name: "provider-credential",
		basePath: "/api/provider",
		description:
			"Providers, models, authentication profiles, provider auth policy, system auth pools/assignments, routing, provider invocation",
	},
	{
		name: "project-session",
		basePath: "/api/session",
		description: "Projects, agents, UCS, vertical packages, agent sessions, chat",
	},
	{
		name: "workflow-agent",
		basePath: "/api/workflow",
		description:
			"Workflow definitions, executions, stages/nodes/attempts, dynamic subtasks, gates, wait handles, checkpoints, effects, progress events",
	},
	{
		name: "artifact-storage",
		basePath: "/api/artifact",
		description: "Workspaces, artifacts, object storage, ownership transfer",
	},
	{
		name: "cost-observability",
		basePath: "/api/observability",
		description: "Cost events and summaries, audit events, error events",
	},
	{
		name: "orders",
		basePath: "/api/orders",
		description:
			"Work orders, questions, append-only records, evidence, role sessions, durable jobs",
	},
	{
		name: "task-board",
		basePath: "/api/task-board",
		description:
			"Internal ops TaskBoard cards with owner-only permissions, detail text, and LLM chat",
	},
] as const;
